import { z } from 'zod'

import {
  billingPeriodSchema,
  idSchema,
  isoDateSchema,
  isoInstantSchema,
  moneyPaisa,
  planCodeSchema,
  planFeatureSchema,
  roleSchema,
  slugSchema,
  subscriptionStatusSchema,
  tenantStatusSchema,
  usernameSchema,
} from '../common'
import { limitsSchema } from './me'

/**
 * U1–U3 · The trader's own account (CB-0005, beyond plan §5).
 *
 * `/me` is the capability token every client reads on launch and after every
 * unlock; this is the page behind the person icon, read only when it is opened.
 * Keeping the two apart keeps a phone number, a billing date and a plan price
 * out of a payload the console and every Staff phone fetch constantly.
 *
 * **`subscription` is ABSENT, not null, for anyone but an Owner** — the same
 * rule as money (§4.16): a Staff phone is not told what the business pays, it
 * is simply not sent. `null` means something else: an Owner whose book has no
 * live subscription row. And inside it `pricePaisa` is money like any other, so
 * a price-locked Owner does not receive it until he unlocks.
 */

export const accountUserSchema = z.object({
  id: idSchema,
  name: z.string(),
  username: usernameSchema,
  role: roleSchema,
  /** Set at signup for an Owner; usually null for people an Owner added. */
  phone: z.string().nullable(),
  mustChangePassword: z.boolean(),
})

export const accountBusinessSchema = z.object({
  name: z.string(),
  /** The business code he signs in with. */
  slug: slugSchema,
  status: tenantStatusSchema,
  createdAt: isoInstantSchema,
  trialEndsAt: isoInstantSchema.nullish(),
})

export const accountSubscriptionSchema = z.object({
  plan: z.object({
    code: planCodeSchema,
    name: z.string(),
    pricePaisa: moneyPaisa(),
    billingPeriod: billingPeriodSchema,
    features: z.array(planFeatureSchema),
  }),
  status: subscriptionStatusSchema,
  currentPeriodStart: isoDateSchema.nullable(),
  currentPeriodEnd: isoDateSchema.nullable(),
  nextChargeOn: isoDateSchema.nullable(),
  cancelAtPeriodEnd: z.boolean(),
})

/* ── U1 · GET /account ────────────────────────────────────────────────────── */

export const accountResponse = z.object({
  user: accountUserSchema,
  business: accountBusinessSchema,
  subscription: accountSubscriptionSchema.nullable().optional(),
  limits: limitsSchema,
  /**
   * CB-0014 · What deleting this account would take with it. The phone warns
   * BEFORE the request, and only the server knows whether another active Owner
   * would keep the book running — so it is sent, not re-derived from a role.
   */
  deletion: z.object({
    /** True for the book's only active Owner: U4 would delete the business too. */
    endsBusiness: z.boolean(),
  }),
})

/* ── U2 · PATCH /account ──────────────────────────────────────────────────── */

/**
 * His own name, and nothing else. There is no id: the server renames the
 * signed-in user, so this cannot be pointed at anyone. Username, role and phone
 * are not accepted — unknown keys are stripped before the handler sees them.
 */
export const patchAccountRequest = z.object({
  name: z.string().trim().min(2).max(120),
})

/* ── U3 · PATCH /account/business ─────────────────────────────────────────── */

/** The business name. Owner only, on every plan — it is identity, not a console feature. */
export const patchAccountBusinessRequest = z.object({
  name: z.string().trim().min(2).max(120),
})

/* ── U4 · POST /account/delete ────────────────────────────────────────────── */

/**
 * CB-0014 · Apple 5.1.1(v): a trader deletes his own account from inside the
 * app. The current password proves it is him; there is no id, so it can only
 * ever delete the signed-in user. POST, not DELETE, because it carries a body.
 */
export const deleteAccountRequest = z.object({
  password: z.string().min(1).max(200),
})

/** `BUSINESS` when he was the book's only active Owner and the book went with him. */
export const deleteAccountResponse = z.object({
  deleted: z.enum(['USER', 'BUSINESS']),
})

export type AccountResponse = z.infer<typeof accountResponse>
export type AccountUser = z.infer<typeof accountUserSchema>
export type AccountBusiness = z.infer<typeof accountBusinessSchema>
export type AccountSubscription = z.infer<typeof accountSubscriptionSchema>
export type PatchAccountRequest = z.input<typeof patchAccountRequest>
export type PatchAccountBusinessRequest = z.input<typeof patchAccountBusinessRequest>
export type DeleteAccountRequest = z.input<typeof deleteAccountRequest>
export type DeleteAccountResponse = z.infer<typeof deleteAccountResponse>
